import styled from 'styled-components';
import { BaseSyntheticEvent, useEffect, useState, Fragment } from 'react';
import { Tooltip } from 'components/common/tooltip';
import { Icon } from 'components/common/icon/icon';
import {
  isMutuallyExclusiveAddon,
  getMutuallyExclusiveGroup,
  MUTUALLY_EXCLUSIVE_ADDONS
} from 'constants/plan';
import type { PackagePrices } from 'types/join';
import AdditionalUsersBox from './additional-users-box';

// Radio button styled components
const RadioButton = styled.input.attrs({ type: 'radio' })`
  width: 12px;
  height: 12px;
  margin-right: 8px;
  background-color: ${props => props.theme.colors.white};
  border-radius: 50%;
  vertical-align: middle;
  border: 1px solid #575757;
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;

  &:hover,
  &:checked {
    border-color: #009ed3;
    background-color: ${props => props.theme.colors.white};

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 6px;
      height: 6px;
      background-color: #009ed3;
      border-radius: 50%;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #cccccc;
  }
`;

const AddonsHeader = styled.div`
  font-size: 18px;
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-weight: 600;
  line-height: 1.2;
  padding-bottom: 12px;
  margin-top: 26px;
  text-align: center;
  color: ${props => props.theme.colors.black};
`;

const HorizontalWrapper = styled.div<{ alignEnd?: boolean; marginTop?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  ${props => props.alignEnd && `align-items: flex-end;`}
  ${props => props.marginTop && `margin-top: ${props.marginTop};`}
`;

const Text = styled.div<{
  color?: string;
  bold?: boolean;
  regular?: boolean;
  size?: number;
  textAlign?: string;
  paddingTop?: string;
  padding?: string;
  cursor?: string;
  noWrap?: boolean;
}>`
  color: ${props => props.color ?? props.theme.colors.siteBlack};
  font-size: ${props => props.size ?? 11}px;
  line-height: 1.25;
  text-align: ${props => props.textAlign ?? 'unset'};
  font-family: ${props => {
    if (props.bold) {
      return props.theme.fonts.interSansBold;
    }

    if (props.regular) {
      return props.theme.fonts.interSans;
    }
    return props.theme.fonts.interSansMedium;
  }};
  ${props => props.paddingTop && `padding-top: ${props.paddingTop}`};
  ${props => props.padding && `padding: ${props.padding}`};
  ${props => props.cursor && `cursor: ${props.cursor}`};
  ${props => props.noWrap && `white-space: nowrap;`};
`;

const LightLine = styled.div`
  display: none;
`;

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AddonNameWrapper = styled(TooltipWrapper)`
  flex: 1;
  min-width: 0;
  gap: 4px;
`;

const AddonActionsWrapper = styled(TooltipWrapper)`
  flex-shrink: 0;
  gap: 4px;
`;

const AdditionalUserWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ErrorAdditionalUser = styled.div`
  font-size: 11px;
  margin-left: 16px;
  color: ${props => props.theme.colors.red};
`;

const IconWrapper = styled.div<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 22px;
  padding: 0 9px;
  border-radius: 4px;
  margin-left: 4px;
  background-color: ${props => (props.disabled ? '#cccccc' : props.theme.colors.siteIndigo)};
  color: white;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
`;

const PlusIconWrapper = styled(IconWrapper)<{ disabled?: boolean }>`
  background-color: ${props => (props.disabled ? '#cccccc' : props.theme.colors.siteIndigo)};

  &:hover {
    background-color: ${props => (props.disabled ? '#cccccc' : props.theme.colors.siteIndigoHover)};
  }
`;

const DeleteIconWrapper = styled(IconWrapper)<{ disabled?: boolean }>`
  min-width: 58px;
  background-color: ${props => (props.disabled ? '#cccccc' : props.theme.colors.siteCardBorder)};
  color: ${props => props.theme.colors.siteBlack};
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  padding: 0 8px;
`;

const TooltipIcon = styled(Icon)`
  display: none;
`;

interface AddonsSectionProps {
  availableAddons: Array<PackagePrices | undefined>;
  selectedAddons: Array<PackagePrices>;
  setSelectedAddons: (addons: Array<PackagePrices>) => void;
  isActive: boolean;
  isAddonSelected: (packageId: string) => boolean;
  findAndSelectAddon: (packageId: string) => void;
  findAndUnselectAddon: (packageId: string) => void;
  getAddonName: (addon: PackagePrices) => string;
  getAddonText: (packageName: string) => string | undefined;
  selectedBilledOption: string;
  numOfAdditionalUser: string;
  setNumber: (e: BaseSyntheticEvent, addonPackage: PackagePrices) => void;
  errorAdditionalUser: boolean;
  TRANSLATION_TEXT: { [key: string]: string };
}

export const AddonsSection = ({
  availableAddons,
  selectedAddons,
  setSelectedAddons,
  isActive,
  isAddonSelected,
  findAndSelectAddon,
  findAndUnselectAddon,
  getAddonName,
  getAddonText,
  selectedBilledOption,
  numOfAdditionalUser,
  setNumber,
  errorAdditionalUser,
  TRANSLATION_TEXT
}: AddonsSectionProps) => {
  // Initialize selectedRadio as an object with default values for each group
  const [selectedRadio, setSelectedRadio] = useState<Record<string, string | undefined>>(() => {
    const initialState: Record<string, string | undefined> = {};
    MUTUALLY_EXCLUSIVE_ADDONS.forEach(group => {
      const selectedGroupAddon = selectedAddons.find(addon =>
        group.addons.includes(addon.packageName)
      );
      initialState[group.groupId] = selectedGroupAddon?.packageName ?? group.defaultSelected;
    });
    return initialState;
  });

  useEffect(() => {
    // Filter selected addons based on all mutually exclusive groups
    const filteredAddons = selectedAddons.filter(addon => {
      const addonName = addon?.packageName ?? '';
      const group = getMutuallyExclusiveGroup(addonName);

      if (!group) {
        // If not part of any mutually exclusive group, keep it
        return true;
      }

      // If part of a mutually exclusive group, only keep if it matches the selected radio for that group
      return selectedRadio[group.groupId] === addonName;
    });

    setSelectedAddons(filteredAddons);
  }, [selectedRadio, setSelectedAddons]);

  const handleRadioChange = (groupId: string, addonName: string) => {
    setSelectedRadio(prev => ({
      ...prev,
      [groupId]: addonName
    }));
  };

  const handleAddAddonClick = (addon: PackagePrices | undefined, groupId?: string) => {
    if (addon) {
      if (groupId) {
        setSelectedRadio(prev => ({
          ...prev,
          [groupId]: addon.packageName
        }));
      }
      findAndSelectAddon(addon.packageId);
    }
  };

  return (
    <>
      {availableAddons.length > 0 && (
        <AddonsHeader>{TRANSLATION_TEXT.available_addons}</AddonsHeader>
      )}
      {availableAddons.map((addon, i, { length }) => {
        const isSelectedAddon = isAddonSelected(addon?.packageId ?? '');
        const isMutuallyExclusive = isMutuallyExclusiveAddon(addon?.packageName ?? '');
        const group = getMutuallyExclusiveGroup(addon?.packageName ?? '');
        const isUnselectedRadio =
          isMutuallyExclusive && group && selectedRadio[group.groupId] !== addon?.packageName;
        const atLeastOneSelected = group && selectedRadio[group.groupId] !== undefined;

        return (
          <Fragment key={addon?.packageId}>
            <HorizontalWrapper>
              <AdditionalUserWrapper>
                <AddonNameWrapper>
                  {isMutuallyExclusive && group && (
                    <RadioButton
                      id={addon?.packageName}
                      key={addon?.packageName}
                      checked={selectedRadio[group.groupId] === addon?.packageName}
                      onChange={() => handleRadioChange(group.groupId, addon?.packageName ?? '')}
                    />
                  )}
                  <Text
                    color={isUnselectedRadio && atLeastOneSelected ? '#9B9B9B' : undefined}
                    size={13}
                    textAlign="left"
                    regular
                    noWrap
                    {...(isMutuallyExclusive &&
                      group && {
                        onClick: () => handleRadioChange(group.groupId, addon?.packageName ?? ''),
                        cursor: 'pointer'
                      })}
                  >
                    {addon && getAddonName(addon)}
                  </Text>
                  <Tooltip
                    black
                    tooltip={
                      TRANSLATION_TEXT[getAddonText(addon?.packageName ?? '') ?? 'empty_text']
                    }
                  >
                    <TooltipIcon name="info" />
                  </Tooltip>
                  {addon?.packageName === 'Member Addon' && isSelectedAddon && (
                    <>
                      {' '}
                      <AdditionalUsersBox
                        isActive={isActive}
                        numOfAdditionalUser={numOfAdditionalUser}
                        setNumber={setNumber}
                        addon={addon}
                      />
                    </>
                  )}
                </AddonNameWrapper>
                {addon?.packageName === 'Member Addon' && errorAdditionalUser && (
                  <ErrorAdditionalUser>
                    {TRANSLATION_TEXT.must_be_a_number_no_greater_than_50}
                  </ErrorAdditionalUser>
                )}
              </AdditionalUserWrapper>
              <AddonActionsWrapper>
                <Text
                  size={13}
                  textAlign="left"
                  regular
                  color={isUnselectedRadio && atLeastOneSelected ? '#9B9B9B' : undefined}
                >
                  ${addon?.price}/
                </Text>
                <Text
                  size={12}
                  textAlign="left"
                  regular
                  color={isUnselectedRadio && atLeastOneSelected ? '#9B9B9B' : undefined}
                >
                  {selectedBilledOption === TRANSLATION_TEXT.billed_annually ||
                  selectedBilledOption === TRANSLATION_TEXT.one_year
                    ? TRANSLATION_TEXT.year
                    : TRANSLATION_TEXT.month}
                </Text>
                {isSelectedAddon ? (
                  <DeleteIconWrapper
                    onClick={() => findAndUnselectAddon(addon?.packageId ?? '')}
                    disabled={!isActive}
                  >
                    Remove
                  </DeleteIconWrapper>
                ) : (
                  <PlusIconWrapper
                    onClick={() => handleAddAddonClick(addon, group?.groupId)}
                    disabled={
                      !isActive ||
                      (isMutuallyExclusive &&
                        !!atLeastOneSelected &&
                        !!group &&
                        selectedRadio[group.groupId] !== addon?.packageName)
                    }
                  >
                    <span>+ Add</span>
                  </PlusIconWrapper>
                )}
              </AddonActionsWrapper>
            </HorizontalWrapper>
            {i + 1 !== length && <LightLine />}
          </Fragment>
        );
      })}
    </>
  );
};
